import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase/config';
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  getDocs,
  where,
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import TripGame from './TripGame';
import LeftAccordion from './LeftAccordion';
import AlbumGrid from './AlbumGrid';
import AlbumDetail from './AlbumDetail';
import RoadTripChat from './RoadTripChat';
import VideoCall from './VideoCall';
import YouTubePlayer from './YouTubePlayer';
import SpotifyPlayer from './SpotifyPlayer';
import { youtubeAPI } from '../services/youtube';
import { spotifyAPI } from '../services/spotify';
import { youtubeAuth } from '../services/youtube';
import { spotifyAuth } from '../services/spotify';
import './TripSession.css';
import './LibraryLayout.css';

// Admin emails that can take hosting from any user
const ADMIN_EMAILS = [
  'mario@google.com',
  'memo@google.com',
  'mvc757@gmail.com',
];

function TripSession({ trip, user, onLeave }) {
  const [tripData, setTripData] = useState(trip);
  const [queue, setQueue] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAddSong, setShowAddSong] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);
  const [showSavePlaylist, setShowSavePlaylist] = useState(false);
  const [playlistName, setPlaylistName] = useState('');
  const [availablePlaylists, setAvailablePlaylists] = useState([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');
  const [isAppending, setIsAppending] = useState(false);
  const playlistIdRef = useRef(trip.playlistId || null);
  const [showHostTransfer, setShowHostTransfer] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  // Track which songs have already had games to prevent restarting
  const gamesPlayedRef = useRef(new Set());
  // Track previous queue to detect newly added songs
  const prevQueueRef = useRef([]);

  // Library navigation state (for all participants) - matching LibraryLayout structure
  const [selectedPath, setSelectedPath] = useState({
    type: null,
    language: null,
    genre: null,
    subgenre: null,
    artist: null,
  });
  const [hierarchy, setHierarchy] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  // Accordion states matching LibraryLayout
  const [isNavbarExpanded, setIsNavbarExpanded] = useState(true); // Expanded by default
  const [isAlbumsExpanded, setIsAlbumsExpanded] = useState(false); // Collapsed by default
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);

  // Queue selection state (for bulk operations)
  // All tracks default to unchecked - users must explicitly select them
  const [selectedSongs, setSelectedSongs] = useState(new Set());

  // Helper function to convert queue songs to playlist format
  const convertQueueToPlaylistTracks = (queueSongs) => {
    return queueSongs.map((song) => {
      // Extract album info from storage path first (for type, language, genre, etc.)
      const pathParts = song.storagePath?.split('/') || [];
      const type = pathParts[0] || 'Albums';
      const language = pathParts[1] || 'English';
      const genre = pathParts[2] || 'Various';
      const subgenre = pathParts[3] || 'Variety';
      const artist = pathParts[4] || song.artist || 'Unknown';
      
      // Prefer album name from song.album (queue item stores album name as a string field)
      // This preserves album names with dashes like "1971 - Ben"
      // Only fall back to extracting from path if song.album is not available
      const albumNameFromQueue = typeof song.album === 'string' && song.album 
        ? song.album 
        : (song.album?.album || song.albumName || 'Unknown');
      
      const albumName = albumNameFromQueue !== 'Unknown' && albumNameFromQueue !== ''
        ? albumNameFromQueue 
        : (pathParts[5] || 'Unknown');

      // Get filename from storage path (last part)
      const filename = pathParts[pathParts.length - 1] || song.title;

      // filePath should be the path WITHOUT the filename (for MediaPlayer)
      // MediaPlayer expects filePath WITHOUT the type prefix (e.g., "English/Rock/..." not "Albums/English/Rock/...")
      // MediaPlayer will add the type prefix itself
      let filePathWithoutFilename;
      if (song.storagePath) {
        // Remove filename from storagePath
        const pathWithoutFilename = song.storagePath.substring(0, song.storagePath.lastIndexOf('/'));
        // Remove type prefix (e.g., "Albums/" or "Singles/") if present
        // MediaPlayer expects: "English/Rock/Classic/Beatles/[Album]/"
        filePathWithoutFilename = pathWithoutFilename.startsWith(`${type}/`)
          ? pathWithoutFilename.substring(type.length + 1) // Remove "Albums/" or "Singles/"
          : pathWithoutFilename;
      } else {
        // Construct path without type prefix
        filePathWithoutFilename = `${language}/${genre}/${subgenre}/${artist}/${albumName}`;
      }

      return {
        Title: filename,
        DisplayTitle: song.title,
        Duration: song.duration || 0,
        Id: song.id || `roadtrip-${song.title}-${Date.now()}`,
        storagePath: song.storagePath,
        album: {
          id: song.album?.id || `roadtrip-album-${albumName}`,
          album: albumName,
          artist: artist,
          language: language,
          genre: genre,
          subgenre: subgenre,
          type: type,
          filePath: filePathWithoutFilename,
          coverUrl: song.album?.coverUrl || null,
        },
      };
    });
  };

  // Auto-update the playlist whenever queue changes
  const updatePlaylist = async (queueSongs) => {
    const currentPlaylistId = playlistIdRef.current;
    if (!currentPlaylistId) return; // No playlist linked yet

    try {
      const tracksToSave = convertQueueToPlaylistTracks(queueSongs);

      await updateDoc(doc(db, 'playlists', currentPlaylistId), {
        tracks: tracksToSave,
        trackCount: tracksToSave.length,
        updatedAt: new Date().toISOString(),
      });

      console.log(
        `✅ Auto-updated playlist with ${tracksToSave.length} track(s)`
      );
    } catch (error) {
      console.error('❌ Error auto-updating playlist:', error);
      // Don't show alert for auto-updates, just log
    }
  };

  const isHost = tripData.hostId === user.uid;
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  // Listen to trip updates
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'roadTrips', trip.id), (doc) => {
      if (doc.exists()) {
        const updatedData = { id: doc.id, ...doc.data() };
        setTripData(updatedData);
        // Update ref with latest playlistId
        playlistIdRef.current = updatedData.playlistId || null;

        // Sync game state from Firestore to local state
        if (updatedData.activeGame) {
          // Only update if it's different to avoid unnecessary re-renders
          setActiveGame((current) => {
            const currentGameId = current?.song?.id || current?.song?.title;
            const newGameId =
              updatedData.activeGame?.song?.id ||
              updatedData.activeGame?.song?.title;
            if (currentGameId !== newGameId) {
              console.log(
                '🎮 Game state synced from Firestore:',
                updatedData.activeGame
              );
              return updatedData.activeGame;
            }
            return current;
          });
        } else {
          // Game was cleared in Firestore, clear local state
          setActiveGame((current) => {
            if (current) {
              console.log('🎮 Game cleared in Firestore');
              return null;
            }
            return current;
          });
        }
      }
    });
    return () => unsubscribe();
  }, [trip.id]);

  // Update current track when currentTrackIndex changes (synced from host)
  useEffect(() => {
    if (queue.length === 0) return;

    const trackIndex = tripData.currentTrackIndex || 0;
    if (queue[trackIndex] && queue[trackIndex].id !== currentTrack?.id) {
      console.log(
        `🎵 Updating current track to index ${trackIndex}:`,
        queue[trackIndex]
      );
      setCurrentTrack(queue[trackIndex]);
    }
  }, [tripData.currentTrackIndex, queue, currentTrack?.id]);

  // Helper to look up DisplayTitle from album for queue items missing it
  const enrichQueueItemWithDisplayTitle = async (song) => {
    // If DisplayTitle already exists, no need to look up
    if (song.DisplayTitle) {
      console.log(
        `✅ Queue item ${song.id} already has DisplayTitle: ${song.DisplayTitle}`
      );
      return song;
    }

    // If no storagePath, can't look up
    if (!song.storagePath) {
      console.log(
        `⚠️ Queue item ${song.id} has no storagePath, cannot look up DisplayTitle`
      );
      return song;
    }

    try {
      // Parse storagePath: Type/Language/Genre/Subgenre/Artist/Album/filename
      const pathParts = song.storagePath.split('/');
      if (pathParts.length < 7) {
        console.log(
          `⚠️ Invalid storagePath for queue item ${song.id}: ${song.storagePath}`
        );
        return song; // Invalid path
      }

      const type = pathParts[0];
      const language = pathParts[1];
      const genre = pathParts[2];
      const subgenre = pathParts[3];
      const artist = pathParts[4];
      const albumName = pathParts[5];
      const filename = pathParts[6]; // Last part is the filename
      console.log(
        `🔍 Looking up DisplayTitle for queue item ${song.id}, filename: ${filename}, current title: ${song.title}`
      );

      // Query albums-flat for the specific album (more efficient than fetching all)
      const albumsRef = collection(db, 'albums-flat');
      // Try to find the album by matching artist and album name
      // Note: We can't query by multiple fields easily, so we'll query by artist first
      const albumQuery = query(
        albumsRef,
        where('artist', '==', artist),
        where('album', '==', albumName)
      );

      try {
        const albumsSnapshot = await getDocs(albumQuery);

        // Find the matching song in the album
        for (const albumDoc of albumsSnapshot.docs) {
          const album = albumDoc.data();
          const matchingSong = album.songs?.find((s) => {
            // Try multiple matching strategies
            const titleMatch = s.Title === filename || s.Title === song.title;
            const titleNormalized = s.Title?.replace(/♫\s*/g, '').trim(); // Remove music note
            const filenameNormalized = filename?.replace(/♫\s*/g, '').trim();
            const normalizedMatch = titleNormalized === filenameNormalized;
            return titleMatch || normalizedMatch;
          });
          if (matchingSong?.DisplayTitle) {
            console.log(
              `✅ Found DisplayTitle for queue item ${song.id}: ${matchingSong.DisplayTitle}`
            );
            song.DisplayTitle = matchingSong.DisplayTitle;

            // Update the queue item in Firestore to persist DisplayTitle
            try {
              await updateDoc(doc(db, 'roadTrips', trip.id, 'queue', song.id), {
                DisplayTitle: matchingSong.DisplayTitle,
              });
              console.log(
                `✅ Updated queue item ${song.id} with DisplayTitle in Firestore`
              );
            } catch (updateError) {
              console.log(
                '❌ Could not update queue item DisplayTitle:',
                updateError.message
              );
            }
            break;
          }
        }
      } catch (queryError) {
        // If the specific query fails, fall back to trying all albums (less efficient)
        console.log(
          `⚠️ Specific album query failed, trying all albums: ${queryError.message}`
        );
        const allAlbumsSnapshot = await getDocs(albumsRef);

        for (const albumDoc of allAlbumsSnapshot.docs) {
          const album = albumDoc.data();
          const matchingSong = album.songs?.find((s) => {
            const titleMatch = s.Title === filename || s.Title === song.title;
            const titleNormalized = s.Title?.replace(/♫\s*/g, '').trim();
            const filenameNormalized = filename?.replace(/♫\s*/g, '').trim();
            const normalizedMatch = titleNormalized === filenameNormalized;
            return titleMatch || normalizedMatch;
          });
          if (matchingSong?.DisplayTitle) {
            console.log(
              `✅ Found DisplayTitle for queue item ${song.id}: ${matchingSong.DisplayTitle}`
            );
            song.DisplayTitle = matchingSong.DisplayTitle;

            try {
              await updateDoc(doc(db, 'roadTrips', trip.id, 'queue', song.id), {
                DisplayTitle: matchingSong.DisplayTitle,
              });
              console.log(
                `✅ Updated queue item ${song.id} with DisplayTitle in Firestore`
              );
            } catch (updateError) {
              console.log(
                '❌ Could not update queue item DisplayTitle:',
                updateError.message
              );
            }
            break;
          }
        }
      }

      if (!song.DisplayTitle) {
        console.log(`⚠️ Could not find DisplayTitle for queue item ${song.id}`);
      }
    } catch (error) {
      // Handle permission errors gracefully - user might not have access to albums-flat
      if (error.code === 'permission-denied') {
        console.log(
          `⚠️ Permission denied reading albums-flat collection. DisplayTitle lookup skipped for queue item ${song.id}. New songs added will have DisplayTitle.`
        );
      } else {
        console.log(
          '❌ Error looking up DisplayTitle for queue item:',
          error.message
        );
      }
    }

    return song;
  };

  // Listen to queue updates
  useEffect(() => {
    const queueRef = collection(db, 'roadTrips', trip.id, 'queue');
    const q = query(queueRef, orderBy('addedAt', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const songs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log(`📋 Queue updated: ${songs.length} song(s)`, songs);

        // Enrich songs missing DisplayTitle (async, non-blocking)
        const enrichedSongs = await Promise.all(
          songs.map((song) => enrichQueueItemWithDisplayTitle(song))
        );

        // Check for newly added songs and auto-select them
        // Compare with previous queue to find new additions
        const prevIds = new Set(prevQueueRef.current.map((s) => s.id));
        const newSongs = enrichedSongs.filter((s) => !prevIds.has(s.id));

        // If there are new songs, add them to selectedSongs
        if (newSongs.length > 0) {
          console.log(
            `✅ Auto-selecting ${newSongs.length} newly added song(s)`,
            newSongs.map((s) => s.id)
          );
          setSelectedSongs((prevSelected) => {
            const newSet = new Set(prevSelected);
            newSongs.forEach((song) => newSet.add(song.id));
            return newSet;
          });
        }

        // Update the previous queue reference
        prevQueueRef.current = enrichedSongs;

        setQueue(enrichedSongs);

        // Auto-update the playlist whenever queue changes
        updatePlaylist(songs);

        // Set current track based on currentTrackIndex from trip data
        if (songs.length > 0) {
          const trackIndex = tripData.currentTrackIndex || 0;
          if (songs[trackIndex] && songs[trackIndex].id !== currentTrack?.id) {
            console.log(
              `🎵 Setting current track to index ${trackIndex}:`,
              songs[trackIndex]
            );
            setCurrentTrack(songs[trackIndex]);
          } else if (!currentTrack && songs[0]) {
            // Fallback: set first song if no current track
            setCurrentTrack(songs[0]);
          }
        }
      },
      (error) => {
        console.error('❌ Error listening to queue:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
        });
        // Try without ordering as fallback
        const fallbackQuery = query(queueRef);
        onSnapshot(fallbackQuery, (snapshot) => {
          const songs = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log(`📋 Queue (fallback): ${songs.length} song(s)`, songs);
          setQueue(songs);
        });
      }
    );
    return () => unsubscribe();
  }, [trip.id, currentTrack, tripData.currentTrackIndex]);

  // Load audio when track changes (only for local files)
  useEffect(() => {
    // Only load audio URL for local files
    if (currentTrack?.source !== 'local' || !currentTrack?.storagePath) {
      setAudioUrl(null);
      return;
    }

    const loadAudio = async () => {
      try {
        const audioStorageRef = ref(storage, currentTrack.storagePath);
        const url = await getDownloadURL(audioStorageRef);
        setAudioUrl(url);
      } catch (error) {
        console.error('Error loading audio:', error);
        setAudioUrl(null);
      }
    };

    loadAudio();
  }, [currentTrack]);

  // Handle play/pause - sync with Firestore state
  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;

    // Use tripData.isPlaying from Firestore (synced across all participants)
    const shouldPlay = tripData.isPlaying || false;

    if (shouldPlay) {
      // Try to play - handle autoplay restrictions
      audioRef.current
        .play()
        .then(() => {
          console.log('✅ Audio playing on this device');
          setIsPlaying(true);
        })
        .catch((error) => {
          console.warn('⚠️ Autoplay blocked or error:', error);
          // For passengers, show a play button if autoplay is blocked
          setIsPlaying(false);
        });
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [tripData.isPlaying, audioUrl]);

  // Start trip (host only)
  const handleStartTrip = async () => {
    if (!isHost) return;

    // Ensure currentTrackIndex is set to 0 (first song) when starting
    await updateDoc(doc(db, 'roadTrips', trip.id), {
      status: 'active',
      startedAt: serverTimestamp(),
      currentTrackIndex: 0, // Start with first song
      currentTime: 0, // Reset time when starting
    });

    // Set current track locally for host
    const firstTrack = queue.length > 0 ? queue[0] : null;
    if (firstTrack && !currentTrack) {
      setCurrentTrack(firstTrack);
    }

    // Start game before playing first song (if games enabled)
    if (firstTrack) {
      // Use setTimeout to ensure currentTrack state is updated
      setTimeout(async () => {
        await startGame(firstTrack);
        // If games are disabled or conditions aren't met, startGame won't set activeGame
        // In that case, we need to start playing. Check after a delay.
        setTimeout(async () => {
          const checkGameStarted = () => {
            // We'll check if games should be disabled based on tripData
            const gamesEnabled = tripData.gamesEnabled !== false;
            const hasEnoughParticipants =
              tripData.participants && tripData.participants.length >= 2;
            const hasOtherParticipants = tripData.participants?.some(
              (p) => p.uid !== firstTrack.addedBy
            );

            if (
              !gamesEnabled ||
              !hasEnoughParticipants ||
              !hasOtherParticipants
            ) {
              updateDoc(doc(db, 'roadTrips', trip.id), {
                isPlaying: true,
              });
            }
          };
          checkGameStarted();
        }, 300);
      }, 100);
    } else {
      // Set playing state in Firestore so all participants sync
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        isPlaying: true,
      });
    }
  };

  // Start a game before playing the next song
  const startGame = async (track = null) => {
    const trackToUse = track || currentTrack;
    if (!trackToUse) {
      console.warn('⚠️ Cannot start game: no current track');
      return;
    }

    // Don't start a game if one is already active
    if (activeGame) {
      console.log('🎮 Game already active, skipping');
      return;
    }

    // Don't start a game if we've already played a game for this track
    if (gamesPlayedRef.current.has(trackToUse.id)) {
      console.log('🎮 Game already played for this track, skipping');
      return;
    }

    // Check if games are enabled (default to true if not set)
    const gamesEnabled = tripData.gamesEnabled !== false; // Default to true

    if (!gamesEnabled) {
      console.log('🎮 Games are disabled for this trip');
      return;
    }

    // Need at least 2 participants to play the game
    if (!tripData.participants || tripData.participants.length < 2) {
      console.warn('⚠️ Cannot start game: need at least 2 participants');
      return;
    }

    const gameData = {
      type: 'whoRequested',
      song: trackToUse,
      options: tripData.participants, // Include all participants - each person will filter out themselves
      correctAnswer: trackToUse.addedBy,
      timeLimit: 10,
      startedAt: serverTimestamp(),
    };

    console.log(
      '🎮 Starting game for track:',
      trackToUse.title,
      'Correct answer:',
      trackToUse.addedBy
    );
    console.log(
      '🎮 Participants in game:',
      tripData.participants.map((p) => ({
        name: p.name,
        email: p.email,
        uid: p.uid,
      }))
    );
    console.log('🎮 Game data:', gameData);

    // Mark this track as having a game started
    gamesPlayedRef.current.add(trackToUse.id);

    // Save game state to Firestore so all participants see it
    try {
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        activeGame: gameData,
      });
      // Local state will be updated via the Firestore listener
    } catch (error) {
      console.error('❌ Error starting game:', error);
      // Fallback to local state if Firestore update fails
      setActiveGame(gameData);
    }
  };

  // Handle game answer
  const handleGameAnswer = async (answer, timeRemaining) => {
    const isCorrect = answer === activeGame.correctAnswer;
    const points = isCorrect
      ? Math.round(100 * (timeRemaining / activeGame.timeLimit))
      : 0;

    if (points > 0) {
      // Update user's score
      const updatedParticipants = tripData.participants.map((p) =>
        p.uid === user.uid ? { ...p, score: (p.score || 0) + points } : p
      );

      await updateDoc(doc(db, 'roadTrips', trip.id), {
        participants: updatedParticipants,
      });
    }

    return { isCorrect, points };
  };

  // Handle game end
  const handleGameEnd = async () => {
    console.log('🎮 Game ended, starting playback');

    // Mark this track as having played a game
    if (currentTrack?.id) {
      gamesPlayedRef.current.add(currentTrack.id);
    }

    // Clear game state in Firestore so all participants see it end
    try {
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        activeGame: null,
        isPlaying: true, // Start playing after game ends
      });
      // Local state will be updated via the Firestore listener
    } catch (error) {
      console.error('❌ Error ending game:', error);
      // Fallback to local state if Firestore update fails
      setActiveGame(null);
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        isPlaying: true,
      });
    }
  };

  // Play next track
  const handleNext = async () => {
    const nextIndex = (tripData.currentTrackIndex || 0) + 1;

    if (nextIndex >= queue.length) {
      // Stop playing when queue ends
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        isPlaying: false,
        currentTrackIndex: nextIndex,
      });
      return;
    }

    await updateDoc(doc(db, 'roadTrips', trip.id), {
      currentTrackIndex: nextIndex,
      currentTime: 0, // Reset time for new track
    });

    const nextTrack = queue[nextIndex];
    setCurrentTrack(nextTrack);

    // Start game before playing next song (if games enabled)
    if (tripData.gamesEnabled && nextTrack) {
      // Use setTimeout to ensure currentTrack state is updated
      setTimeout(async () => {
        await startGame(nextTrack);
      }, 100);
    } else {
      // Continue playing automatically if games disabled
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        activeGame: null, // Clear any existing game
        isPlaying: true,
      });
    }
  };

  // Play previous track
  const handlePrevious = async () => {
    if (!isHost) return;

    const currentIndex = tripData.currentTrackIndex || 0;
    const previousIndex = currentIndex - 1;

    if (previousIndex < 0) {
      // Already at first song, restart it
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        currentTrackIndex: 0,
        currentTime: 0, // Reset time for new track
      });
      const firstTrack = queue[0];
      setCurrentTrack(firstTrack);

      // Start game before playing if games enabled
      if (tripData.gamesEnabled && firstTrack) {
        setTimeout(async () => {
          await startGame(firstTrack);
        }, 100);
      } else {
        await updateDoc(doc(db, 'roadTrips', trip.id), {
          activeGame: null, // Clear any existing game
          isPlaying: true,
        });
      }
      return;
    }

    await updateDoc(doc(db, 'roadTrips', trip.id), {
      currentTrackIndex: previousIndex,
      currentTime: 0, // Reset time for new track
    });

    const prevTrack = queue[previousIndex];
    setCurrentTrack(prevTrack);

    // Start game before playing if games enabled
    if (tripData.gamesEnabled && prevTrack) {
      setTimeout(async () => {
        await startGame(prevTrack);
      }, 100);
    } else {
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        activeGame: null, // Clear any existing game
        isPlaying: true,
      });
    }
  };

  // Handle clicking on a song in the queue (host only)
  const handleQueueItemClick = async (index) => {
    if (!isHost) return; // Only host can change tracks

    if (index < 0 || index >= queue.length) return;

    // Check if song is selected (enabled)
    const song = queue[index];
    if (!selectedSongs.has(song.id)) {
      alert('This song is disabled. Please check it to enable playback.');
      return;
    }

    try {
      // Update the current track index in Firestore
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        currentTrackIndex: index,
        currentTime: 0, // Reset time for new track
      });

      // Update local state immediately for better UX
      const selectedTrack = queue[index];
      setCurrentTrack(selectedTrack);

      // Determine if we should start a game
      let shouldStartGame = false;
      if (tripData.gamesEnabled && selectedTrack) {
        // Check if game can actually start (needs 2+ participants and game hasn't been played)
        const hasEnoughParticipants = tripData.participants && tripData.participants.length >= 2;
        const gameNotPlayed = !gamesPlayedRef.current.has(selectedTrack.id);
        shouldStartGame = hasEnoughParticipants && gameNotPlayed;
      }

      if (shouldStartGame) {
        // Start game - it will handle playback after game ends
        // Clear any existing game and pause playback while game is active
        await updateDoc(doc(db, 'roadTrips', trip.id), {
          activeGame: null, // Clear any existing game
          isPlaying: false, // Pause while game is active
        });
        setTimeout(async () => {
          await startGame(selectedTrack);
        }, 100);
      } else {
        // No game to start - play immediately
        await updateDoc(doc(db, 'roadTrips', trip.id), {
          activeGame: null, // Clear any existing game
          isPlaying: true, // Start playing immediately
        });
      }

      console.log(`🎵 Host selected track at index ${index}:`, queue[index]);
    } catch (error) {
      console.error('❌ Error selecting track:', error);
      alert(`Failed to select track: ${error.message}`);
    }
  };

  // Add song to queue (from library navigation or search)
  const handleAddSong = async (song, album = null) => {
    try {
      const userName = user.displayName || user.email.split('@')[0];

      // Determine source type
      const source = song.source || 'local';

      // Build storagePath if not provided (from AlbumDetail track clicks) - only for local files
      // ALWAYS rebuild storagePath when album parameter is provided to ensure we use the correct album name
      // This is critical for albums with dashes like "1972 - Ben" where the album name must be preserved exactly
      let storagePath = song.storagePath || song.path;
      if (album && source === 'local') {
        const type = album.type || 'Albums';
        const language = album.language || 'English';
        const genre = album.genre || 'Various';
        const subgenre = album.subgenre || 'Variety';
        const artist = album.artist || 'Unknown';
        
        // Extract album name from filePath if available (most reliable source)
        // filePath format: "English/R and B/Pop/Michael Jackson/1972 - Ben/"
        // The album name is the last segment before the trailing slash
        let albumName = album.album || 'Unknown';
        if (album.filePath) {
          // Extract the album name from filePath (last path segment)
          const filePathParts = album.filePath.replace(/\/+$/, '').split('/');
          if (filePathParts.length > 0) {
            const extractedAlbumName = filePathParts[filePathParts.length - 1];
            if (extractedAlbumName) {
              albumName = extractedAlbumName;
              console.log('✅ Extracted album name from filePath:', {
                originalAlbum: album.album,
                extractedAlbumName: albumName,
                filePath: album.filePath
              });
            }
          }
        } else if (album.coverUrl) {
          // Fallback: Extract from coverUrl if filePath not available
          // coverUrl format: "/English/R and B/Pop/Michael Jackson/1972 - Ben/album.jpg"
          const coverPathParts = album.coverUrl.replace(/^\/+|\/+$/g, '').split('/');
          if (coverPathParts.length >= 6) {
            // Path structure: [language]/[genre]/[subgenre]/[artist]/[album]/album.jpg
            albumName = coverPathParts[coverPathParts.length - 2]; // Second to last is album name
            console.log('✅ Extracted album name from coverUrl:', {
              originalAlbum: album.album,
              extractedAlbumName: albumName,
              coverUrl: album.coverUrl
            });
          }
        }

        // Get filename - Title is the filename (may include .mp3)
        let filename = song.Title || song.DisplayTitle || 'Unknown';
        if (
          !filename.toLowerCase().endsWith('.mp3') &&
          !filename.toLowerCase().endsWith('.m4a') &&
          !filename.toLowerCase().endsWith('.flac')
        ) {
          filename = filename + '.mp3';
        }

        // Build storage path: Type/Language/Genre/Subgenre/Artist/Album/filename
        // This ensures the full album name (including "1972 - Ben") is preserved
        storagePath = `${type}/${language}/${genre}/${subgenre}/${artist}/${albumName}/${filename}`;
        console.log('🔍 Built storagePath:', {
          originalAlbumField: album.album,
          usedAlbumName: albumName,
          storagePath: storagePath
        });
      }

      // Build queue item data based on source type
      const queueItem = {
        title:
          song.DisplayTitle ||
          song.Title ||
          song.title ||
          song.name ||
          'Unknown Title',
        DisplayTitle:
          song.DisplayTitle ||
          song.Title ||
          song.title ||
          song.name ||
          'Unknown Title',
        artist:
          song.artist ||
          song.album?.artist ||
          album?.artist ||
          'Unknown Artist',
        // Prefer album name from the album parameter (from AlbumDetail) as it's the most reliable source
        // This ensures we get the full album name including "1971 - Ben" format
        album: album?.album || song.album?.album || song.albumName || '',
        source: source,
        addedBy: user.uid,
        addedByName: userName,
        addedAt: serverTimestamp(),
      };

      // Add source-specific fields
      if (source === 'local') {
        queueItem.storagePath = storagePath || '';
      } else if (source === 'youtube') {
        queueItem.videoId = song.videoId || song.id;
        queueItem.thumbnail = song.thumbnail;
      } else if (source === 'spotify') {
        queueItem.uri = song.uri;
        queueItem.spotifyId = song.id?.replace('spotify-', '') || song.id;
        queueItem.thumbnail = song.thumbnail || song.image;
        queueItem.duration = song.duration; // in milliseconds
      }

      // Add song to Firestore queue
      const docRef = await addDoc(
        collection(db, 'roadTrips', trip.id, 'queue'),
        queueItem
      );

      // Automatically check the newly added song
      setSelectedSongs((prev) => {
        const newSet = new Set(prev);
        newSet.add(docRef.id);
        return newSet;
      });

      console.log(`✅ Song added to queue (${source}):`, queueItem.title);
    } catch (error) {
      console.error('❌ Error adding song to queue:', error);
      alert(`Failed to add song: ${error.message}`);
    }
  };

  // Handle track selection from AlbumDetail (adds to queue)
  const handleTrackSelect = (song, album) => {
    handleAddSong(song, album);
  };

  // Library navigation handlers
  const handlePathChange = (path) => {
    setSelectedPath(path);
    setSelectedAlbum(null); // Clear album selection when path changes
  };

  const handleAlbumSelect = (album) => {
    setSelectedAlbum(album);
  };

  // Navigation accordion handler (matching LibraryLayout)
  const handleNavigationClick = () => {
    setIsNavbarExpanded(!isNavbarExpanded);
    setLeftPanelCollapsed(false);
  };

  // Queue selection handlers (host only)
  const handleToggleSelection = (songId, e) => {
    e.stopPropagation(); // Prevent triggering queue item click

    // Only host can toggle selection
    if (!isHost) {
      return;
    }

    setSelectedSongs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(songId)) {
        newSet.delete(songId);
      } else {
        newSet.add(songId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedSongs(new Set(queue.map((song) => song.id)));
  };

  const handleDeselectAll = () => {
    setSelectedSongs(new Set());
  };

  // Bulk delete selected songs (all participants can delete)
  const handleDeleteSelected = async () => {
    if (selectedSongs.size === 0) return;

    if (
      !window.confirm(`Remove ${selectedSongs.size} song(s) from the queue?`)
    ) {
      return;
    }

    try {
      const currentIndex = tripData.currentTrackIndex || 0;
      let deletedCount = 0;
      let newIndex = currentIndex;

      // Delete all selected songs
      for (const songId of selectedSongs) {
        const songIndex = queue.findIndex((s) => s.id === songId);
        if (songIndex >= 0) {
          await deleteDoc(doc(db, 'roadTrips', trip.id, 'queue', songId));
          deletedCount++;

          // Adjust currentTrackIndex if needed
          if (songIndex < currentIndex) {
            newIndex = Math.max(0, newIndex - 1);
          } else if (songIndex === currentIndex) {
            // If we deleted the current song, move to previous
            newIndex = Math.max(0, currentIndex - 1);
          }
        }
      }

      // Update currentTrackIndex if it changed
      if (newIndex !== currentIndex) {
        await updateDoc(doc(db, 'roadTrips', trip.id), {
          currentTrackIndex: newIndex,
        });
      }

      // Clear selection
      setSelectedSongs(new Set());
      console.log(`✅ Removed ${deletedCount} song(s) from queue`);
    } catch (error) {
      console.error('❌ Error removing songs from queue:', error);
      alert(`Failed to remove songs: ${error.message}`);
    }
  };

  // Delete song from queue (all participants can delete)
  const handleDeleteSong = async (songId, songIndex, e) => {
    // Stop event propagation so it doesn't trigger the queue item click
    e.stopPropagation();

    if (!window.confirm('Remove this song from the queue?')) {
      return;
    }

    try {
      const currentIndex = tripData.currentTrackIndex || 0;

      // Delete the song from Firestore
      await deleteDoc(doc(db, 'roadTrips', trip.id, 'queue', songId));

      // If we deleted the current playing song or a song before it, update currentTrackIndex
      if (songIndex <= currentIndex) {
        const newIndex = Math.max(0, currentIndex - 1);
        await updateDoc(doc(db, 'roadTrips', trip.id), {
          currentTrackIndex: newIndex,
        });
      }

      console.log('✅ Song removed from queue');
    } catch (error) {
      console.error('❌ Error removing song from queue:', error);
      alert(`Failed to remove song: ${error.message}`);
    }
  };

  // Search for songs from your library, YouTube, and Spotify
  const handleSearch = async (searchQuery) => {
    setSearchQuery(searchQuery);

    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const results = [];

    try {
      // 1. Search local library (albums-flat collection)
      const albumsRef = collection(db, 'albums-flat');
      const albumsSnapshot = await getDocs(albumsRef);

      const queryLower = searchQuery.toLowerCase();

      albumsSnapshot.forEach((albumDoc) => {
        const album = albumDoc.data();

        // Check if album has songs
        if (!album.songs || !Array.isArray(album.songs)) return;

        // Search through songs in this album
        album.songs.forEach((song) => {
          const songTitle = song.Title || song.DisplayTitle || '';
          const songTitleLower = songTitle.toLowerCase();
          const artist = album.artist || '';
          const artistLower = artist.toLowerCase();
          const albumName = album.album || album.albumName || '';
          const albumNameLower = albumName.toLowerCase();

          // Match if query appears in title, artist, or album name
          if (
            songTitleLower.includes(queryLower) ||
            artistLower.includes(queryLower) ||
            albumNameLower.includes(queryLower)
          ) {
            // Build storage path
            const type = album.type || 'Albums';
            const language = album.language || 'English';
            const genre = album.genre || 'Various';
            const subgenre = album.subgenre || 'Variety';
            const artistName = album.artist || 'Unknown';
            const albumNameFinal = album.album || album.albumName || 'Unknown';

            // Get filename - Title is the filename
            let filename = song.Title || song.DisplayTitle || 'Unknown';
            if (
              !filename.toLowerCase().endsWith('.mp3') &&
              !filename.toLowerCase().endsWith('.m4a') &&
              !filename.toLowerCase().endsWith('.flac')
            ) {
              filename = filename + '.mp3';
            }

            // Build storage path: Type/Language/Genre/Subgenre/Artist/Album/filename
            const storagePath = `${type}/${language}/${genre}/${subgenre}/${artistName}/${albumNameFinal}/${filename}`;

            results.push({
              id: `local-${albumDoc.id}-${songTitle}`,
              title: song.DisplayTitle || songTitle,
              artist: artistName,
              album: {
                album: albumNameFinal,
                artist: artistName,
              },
              storagePath: storagePath,
              path: storagePath, // Alias for compatibility
              source: 'local',
            });
          }
        });
      });

      // 2. Search YouTube (if authenticated)
      if (youtubeAuth.isAuthenticated()) {
        try {
          const youtubeResults = await youtubeAPI.search(searchQuery, null, 10);
          youtubeResults.items.forEach((item) => {
            results.push({
              id: `youtube-${item.id}`,
              title: item.title,
              artist: item.artist,
              thumbnail: item.thumbnail,
              videoId: item.id,
              source: 'youtube',
            });
          });
          console.log(
            `🔍 Found ${youtubeResults.items.length} YouTube result(s)`
          );
        } catch (youtubeError) {
          console.error('❌ Error searching YouTube:', youtubeError);
        }
      }

      // 3. Search Spotify (if authenticated)
      if (spotifyAuth.isAuthenticated()) {
        try {
          const spotifyResults = await spotifyAPI.search(
            searchQuery,
            ['track'],
            10
          );
          spotifyResults.tracks?.items?.forEach((track) => {
            results.push({
              id: `spotify-${track.id}`,
              title: track.name,
              artist:
                track.artists?.map((a) => a.name).join(', ') ||
                'Unknown Artist',
              thumbnail: track.album?.images?.[0]?.url,
              uri: track.uri,
              duration: track.duration_ms,
              source: 'spotify',
            });
          });
          console.log(
            `🔍 Found ${
              spotifyResults.tracks?.items?.length || 0
            } Spotify result(s)`
          );
        } catch (spotifyError) {
          console.error('❌ Error searching Spotify:', spotifyError);
        }
      }

      // Limit results to 50 to avoid overwhelming the UI
      setSearchResults(results.slice(0, 50));
      console.log(
        `🔍 Found ${results.length} total song(s) matching "${searchQuery}" (${
          results.filter((r) => r.source === 'local').length
        } local, ${
          results.filter((r) => r.source === 'youtube').length
        } YouTube, ${
          results.filter((r) => r.source === 'spotify').length
        } Spotify)`
      );
    } catch (error) {
      console.error('❌ Error searching:', error);
      setSearchResults([]);
    }
  };

  // Load available playlists (including empty Road Trip playlists)
  const loadAvailablePlaylists = async () => {
    try {
      const q = query(
        collection(db, 'playlists'),
        where('userId', '==', user.uid)
      );
      const querySnapshot = await getDocs(q);
      const playlists = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Include all playlists, including empty ones and Road Trip playlists
        playlists.push({ 
          id: doc.id, 
          name: data.name || 'Unnamed Playlist',
          trackCount: data.trackCount || 0,
          source: data.source || 'local',
          tracks: data.tracks || [],
          ...data 
        });
      });

      // Sort by updatedAt or createdAt (most recent first)
      playlists.sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setAvailablePlaylists(playlists);
    } catch (error) {
      console.error('❌ Error loading playlists:', error);
      setAvailablePlaylists([]);
    }
  };

  // Save Road Trip as Playlist (with a different name or append to existing)
  const handleSaveAsPlaylist = async () => {
    if (queue.length === 0) {
      alert('No songs in queue to save');
      return;
    }

    // Stop the media player
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      // If host, also update Firestore to pause for all participants
      if (isHost) {
        try {
          await updateDoc(doc(db, 'roadTrips', trip.id), {
            isPlaying: false,
          });
        } catch (error) {
          console.error('Error updating play state:', error);
        }
      }
      console.log('⏹️ Media player stopped');
    }

    try {
      const tracksToSave = convertQueueToPlaylistTracks(queue);

      if (isAppending && selectedPlaylistId) {
        // Append to existing playlist
        const selectedPlaylist = availablePlaylists.find(p => p.id === selectedPlaylistId);
        if (!selectedPlaylist) {
          alert('Selected playlist not found');
          return;
        }

        // Get existing tracks
        const existingTracks = selectedPlaylist.tracks || [];
        
        // Combine tracks (avoid duplicates based on track key)
        const trackMap = new Map();
        existingTracks.forEach(track => {
          const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${track.album?.album || ''}`;
          trackMap.set(trackKey, track);
        });

        // Add new tracks (skip duplicates)
        tracksToSave.forEach(track => {
          const trackKey = `${track.Title || ''}_${track.album?.artist || ''}_${track.album?.album || ''}`;
          if (!trackMap.has(trackKey)) {
            trackMap.set(trackKey, track);
          }
        });

        const combinedTracks = Array.from(trackMap.values());

        await updateDoc(doc(db, 'playlists', selectedPlaylistId), {
          tracks: combinedTracks,
          trackCount: combinedTracks.length,
          updatedAt: new Date().toISOString(),
        });

        alert(
          `✅ Added ${tracksToSave.length} song(s) to "${selectedPlaylist.name}"! Total: ${combinedTracks.length} songs.`
        );
      } else {
        // Create new playlist
        if (!playlistName.trim()) {
          alert('Please enter a playlist name');
          return;
        }

        const playlistDoc = {
          name: playlistName.trim(),
          description: `Road Trip: ${tripData.name}${
            tripData.purpose ? ` - ${tripData.purpose}` : ''
          }`,
          userId: user.uid,
          userEmail: user.email,
          tracks: tracksToSave,
          trackCount: tracksToSave.length,
          source: 'roadTrip',
          roadTripId: trip.id,
          roadTripName: tripData.name,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        await addDoc(collection(db, 'playlists'), playlistDoc);
        alert(
          `✅ Playlist "${playlistName}" saved! You can find it in your Saved Playlists.`
        );
      }

      setShowSavePlaylist(false);
      setPlaylistName('');
      setSelectedPlaylistId('');
      setIsAppending(false);
      // Reload playlists for next time
      await loadAvailablePlaylists();
    } catch (error) {
      console.error('❌ Error saving playlist:', error);
      alert(`Failed to save playlist: ${error.message}`);
    }
  };

  // End trip (host only)
  const handleEndTrip = async () => {
    if (!isHost) return;

    if (window.confirm('End this road trip?')) {
      await updateDoc(doc(db, 'roadTrips', trip.id), {
        status: 'ended',
        endedAt: serverTimestamp(),
      });
      onLeave();
    }
  };

  // Leave trip (non-host)
  const handleLeaveTrip = async () => {
    if (window.confirm('Leave this road trip?')) {
      const updatedParticipants = tripData.participants.filter(
        (p) => p.uid !== user.uid
      );
      const updatedIds = tripData.participantIds.filter(
        (id) => id !== user.uid
      );

      await updateDoc(doc(db, 'roadTrips', trip.id), {
        participants: updatedParticipants,
        participantIds: updatedIds,
      });
      onLeave();
    }
  };

  // Copy invite link
  const copyInviteCode = () => {
    navigator.clipboard.writeText(tripData.code);
    alert(`Code copied: ${tripData.code}`);
  };

  // Request to become host (non-host only)
  const handleRequestHost = async () => {
    if (isHost) return;

    const userName = user.displayName || user.email.split('@')[0];

    if (
      window.confirm(
        `Request to become host? The current host will be notified.`
      )
    ) {
      try {
        // Add a host request to the trip (you could also use a chat/notification system)
        // For now, we'll directly transfer if host approves, or show a message
        alert(
          'Host transfer request sent! The current host can transfer control to you.'
        );

        // Optionally, you could add a hostRequests array to the trip document
        // For simplicity, we'll show a transfer dialog to the host
      } catch (error) {
        console.error('❌ Error requesting host:', error);
        alert(`Failed to request host: ${error.message}`);
      }
    }
  };

  // Transfer host to another participant (host only)
  const handleTransferHost = async (newHostUid, newHostName) => {
    if (!isHost) return;

    if (window.confirm(`Transfer host control to ${newHostName}?`)) {
      try {
        // Update participants array to mark new host
        const updatedParticipants = tripData.participants.map((p) => ({
          ...p,
          isHost: p.uid === newHostUid,
        }));

        await updateDoc(doc(db, 'roadTrips', trip.id), {
          hostId: newHostUid,
          hostName: newHostName,
          participants: updatedParticipants,
        });

        alert(`✅ Host control transferred to ${newHostName}`);
        setShowHostTransfer(false);
      } catch (error) {
        console.error('❌ Error transferring host:', error);
        alert(`Failed to transfer host: ${error.message}`);
      }
    }
  };

  // Admin function to take hosting from any user
  const handleAdminTakeHost = async () => {
    if (!isAdmin) return;

    const userName = user.displayName || user.email.split('@')[0];

    if (
      window.confirm(
        `Take host control as ${userName}? This will transfer control from the current host.`
      )
    ) {
      try {
        // Update participants array to mark admin as host
        const updatedParticipants = tripData.participants.map((p) => ({
          ...p,
          isHost: p.uid === user.uid,
        }));

        // If admin is not in participants, add them
        if (!tripData.participants.find((p) => p.uid === user.uid)) {
          updatedParticipants.push({
            uid: user.uid,
            name: userName,
            email: user.email,
            isHost: true,
            score: 0,
            joinedAt: new Date().toISOString(),
          });
        }

        await updateDoc(doc(db, 'roadTrips', trip.id), {
          hostId: user.uid,
          hostName: userName,
          participants: updatedParticipants,
          participantIds: tripData.participantIds?.includes(user.uid)
            ? tripData.participantIds
            : [...(tripData.participantIds || []), user.uid],
        });

        alert(`✅ Host control taken by ${userName}`);
      } catch (error) {
        console.error('❌ Error taking host control:', error);
        alert(`Failed to take host control: ${error.message}`);
      }
    }
  };

  return (
    <div className="trip-session">
      {/* Header */}
      <div className="trip-header">
        <div className="trip-actions-row">
          <div className="trip-actions">
            {isHost ? (
              <>
                <button
                  className="save-playlist-btn"
                  onClick={async () => {
                    setShowSavePlaylist(true);
                    await loadAvailablePlaylists();
                  }}
                  title="Save as Playlist with Different Name"
                >
                  <span className="btn-text-full">Save Playlist</span>
                  <span className="btn-text">Save Playlist</span>
                </button>
                <button
                  className="transfer-host-btn"
                  onClick={() => setShowHostTransfer(true)}
                  title="Transfer Host Control"
                >
                  <span className="btn-icon">👑</span>
                  <span className="btn-text-full">Transfer Host</span>
                  <span className="btn-text">Transfer Host</span>
                </button>
              </>
            ) : (
              <>
                {isAdmin && !isHost && (
                  <button
                    className="admin-take-host-btn"
                    onClick={handleAdminTakeHost}
                    title="Admin: Take Host Control"
                  >
                    <span className="btn-icon">⚡</span>
                    <span className="btn-text-full">Take Host (Admin)</span>
                    <span className="btn-text">Take Host</span>
                  </button>
                )}
                <button
                  className="save-playlist-btn"
                  onClick={async () => {
                    setShowSavePlaylist(true);
                    await loadAvailablePlaylists();
                  }}
                  title="Save as Playlist with Different Name"
                >
                  <span className="btn-text-full">Save Playlist</span>
                  <span className="btn-text">Save</span>
                </button>
                <button
                  className="request-host-btn"
                  onClick={handleRequestHost}
                  title="Request to Become Host"
                >
                  👑 Request Host
                </button>
                <button className="leave-btn" onClick={handleLeaveTrip}>
                  Leave
                </button>
              </>
            )}
          </div>
        </div>
        <div className="trip-title-row">
          <button
            className="invite-btn"
            onClick={() => setShowInvite(!showInvite)}
          >
            👥 {tripData.participants?.length || 1}
          </button>
          <button
            className="chat-btn"
            onClick={() => setShowChat(!showChat)}
            title="Open chat"
            style={{ display: 'none' }}
          >
            💬 Chat
          </button>
          <button
            className="video-call-btn"
            onClick={() => setShowVideoCall(!showVideoCall)}
            title="Start video call"
            style={{ display: 'none' }}
          >
            📹 Video
          </button>
          <div className="trip-title">
            <span className="trip-icon">🚗</span>
            <div>
              <h1>{tripData.name}</h1>
              {tripData.purpose && (
                <p className="trip-purpose trip-purpose-desktop">
                  {tripData.purpose}
                </p>
              )}
            </div>
          </div>
          {isHost && (
            <button className="end-btn" onClick={handleEndTrip}>
              <span className="btn-text-full">End Trip</span>
            </button>
          )}
        </div>
      </div>

      {/* Invite Panel */}
      {showInvite && (
        <div className="invite-panel">
          <div className="invite-code">
            <span>Trip Code:</span>
            <strong>{tripData.code}</strong>
            <button onClick={copyInviteCode}>📋 Copy</button>
          </div>
          <div className="participants-list">
            <h3>Passengers ({tripData.participants?.length || 1}/8)</h3>
            {tripData.participants?.map((p, i) => (
              <div key={i} className="participant">
                <span className="participant-avatar">
                  {p.isHost ? '👑' : '🎵'}
                </span>
                <span className="participant-name">{p.name}</span>
                <span className="participant-score">{p.score || 0} pts</span>
                {isHost && !p.isHost && (
                  <button
                    className="make-host-btn"
                    onClick={() => handleTransferHost(p.uid, p.name)}
                    title={`Transfer host to ${p.name}`}
                  >
                    👑
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Game Overlay */}
      {activeGame && (
        <TripGame
          game={activeGame}
          participants={tripData.participants}
          user={user}
          onAnswer={handleGameAnswer}
          onEnd={handleGameEnd}
        />
      )}

      {/* Now Playing */}
      {currentTrack && !activeGame && (
        <div className="now-playing">
          <div className="now-playing-label">NOW PLAYING</div>
          <div className="now-playing-info">
            <div className="track-title">
              {currentTrack.DisplayTitle || currentTrack.title}
            </div>
            <div className="track-artist">{currentTrack.artist}</div>
            <div className="track-added-by">
              Added by: {currentTrack.addedByName}
            </div>
            {currentTrack.source && (
              <div className="track-source">
                {currentTrack.source === 'local' && '📁 Local'}
                {currentTrack.source === 'youtube' && '▶️ YouTube'}
                {currentTrack.source === 'spotify' && '🎵 Spotify'}
              </div>
            )}
          </div>

          {/* Conditional rendering based on source type */}
          {currentTrack.source === 'local' && (
            <>
              {isHost && (
                <div className="playback-controls">
                  <button onClick={handlePrevious} title="Previous Song">
                    ⏮️
                  </button>
                  <button
                    onClick={async () => {
                      const newPlayingState = !tripData.isPlaying;
                      await updateDoc(doc(db, 'roadTrips', trip.id), {
                        isPlaying: newPlayingState,
                      });
                    }}
                    title={tripData.isPlaying ? 'Pause' : 'Play'}
                  >
                    {tripData.isPlaying ? '⏸️' : '▶️'}
                  </button>
                  <button onClick={handleNext} title="Next Song">
                    ⏭️
                  </button>
                </div>
              )}
              {!isHost && (
                <div className="playback-status">
                  {tripData.isPlaying ? (
                    isPlaying ? (
                      <span>▶️ Playing</span>
                    ) : (
                      <div>
                        <span>⏸️ Paused (click to play)</span>
                        <button
                          onClick={() => {
                            audioRef.current?.play().catch(console.error);
                          }}
                          style={{ marginLeft: '10px' }}
                        >
                          ▶️ Play
                        </button>
                      </div>
                    )
                  ) : (
                    <span>⏸️ Paused</span>
                  )}
                </div>
              )}
              <audio ref={audioRef} src={audioUrl} onEnded={handleNext} />
            </>
          )}

          {currentTrack.source === 'youtube' && currentTrack.videoId && (
            <YouTubePlayer
              videoId={currentTrack.videoId}
              isPlaying={tripData.isPlaying || false}
              onPlay={() =>
                updateDoc(doc(db, 'roadTrips', trip.id), { isPlaying: true })
              }
              onPause={() =>
                updateDoc(doc(db, 'roadTrips', trip.id), { isPlaying: false })
              }
              onEnded={handleNext}
              onNext={isHost ? handleNext : undefined}
              onPrevious={isHost ? handlePrevious : undefined}
              currentTrack={currentTrack}
              queueLength={queue.length}
              currentIndex={tripData.currentTrackIndex || 0}
              syncTime={tripData.currentTime || null}
              onTimeUpdate={
                isHost
                  ? async (time) => {
                      // Host updates Firestore with current playback time
                      await updateDoc(doc(db, 'roadTrips', trip.id), {
                        currentTime: time,
                      });
                    }
                  : null
              }
              isHost={isHost}
            />
          )}

          {currentTrack.source === 'spotify' && currentTrack.uri && (
            <SpotifyPlayer
              trackUri={currentTrack.uri}
              currentTrack={currentTrack}
              onTrackEnd={handleNext}
              onNext={isHost ? handleNext : undefined}
              onPrevious={isHost ? handlePrevious : undefined}
              queueLength={queue.length}
              currentIndex={tripData.currentTrackIndex || 0}
            />
          )}
        </div>
      )}

      {/* Main Content: Library Navigation + Queue */}
      <div className="trip-main-content">
        {/* Library Navigation Section - Using LibraryLayout's accordion structure */}
        <div className={`library-main ${leftPanelCollapsed ? 'left-collapsed' : ''}`}>
          {/* Viewport Container */}
          <div className="viewport-container" style={{ display: 'flex', visibility: 'visible', opacity: 1 }}>
            {/* Navigation Accordion - Matching LibraryLayout */}
            <div className={`viewport-top-column accordion-section ${!isNavbarExpanded ? 'collapsed' : ''}`}>
              {/* Accordion Header for Navbar */}
              <div
                className="accordion-header"
                onClick={handleNavigationClick}
                style={{
                  padding: '0.75rem 1rem',
                  background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
                  borderBottom: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <span style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}>
                  Navigation
                </span>
                <span
                  style={{
                    color: '#fff',
                    fontSize: '1.2rem',
                    transition: 'transform 0.3s ease',
                    transform: isNavbarExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  ▼
                </span>
              </div>

              {/* Accordion Content */}
              {isNavbarExpanded && (
                <div className="accordion-content" style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className="viewport-nav-content">
                    <LeftAccordion
                      selectedPath={selectedPath}
                      onPathChange={handlePathChange}
                      onHierarchyLoad={setHierarchy}
                      user={user}
                      onClose={() => setLeftPanelCollapsed(true)}
                      onDoneSearching={() => {
                        setIsAlbumsExpanded(true);
                        setIsNavbarExpanded(false);
                      }}
                      isCollapsed={false}
                      onAlbumSelect={handleAlbumSelect}
                      isFocused={false}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Albums Accordion - Matching LibraryLayout */}
            <div className={`viewport-bottom-column accordion-section ${!isAlbumsExpanded ? 'collapsed' : ''}`}
              style={{
                display: 'flex',
                visibility: 'visible',
                opacity: 1,
                width: '100%',
                minHeight: '48px',
                position: 'relative',
                zIndex: 10,
              }}
            >
              {/* Accordion Header for Albums */}
              <div
                className="accordion-header"
                onClick={() => setIsAlbumsExpanded(!isAlbumsExpanded)}
                style={{
                  padding: '0.75rem 1rem',
                  background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
                  borderBottom: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <span style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}>
                  Albums
                </span>
                <span
                  style={{
                    color: '#fff',
                    fontSize: '1.2rem',
                    transition: 'transform 0.3s ease',
                    transform: isAlbumsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  ▼
                </span>
              </div>

              {/* Accordion Content */}
              {isAlbumsExpanded && (
                <div className="accordion-content">
                  <AlbumGrid
                    selectedPath={selectedPath}
                    selectedAlbum={selectedAlbum}
                    onAlbumSelect={handleAlbumSelect}
                    hierarchy={hierarchy}
                    onShufflePlay={() => {}}
                    queuedAlbumIds={new Set()}
                    onAddToQueue={() => {}}
                    onQueueModeChange={() => {}}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Album Detail Panel */}
        {selectedAlbum && (
          <div className="trip-library-detail">
            <AlbumDetail
              album={selectedAlbum}
              currentTrack={null}
              isPlaying={false}
              onPlayTrack={handleTrackSelect}
              onPlayAlbum={() => {}}
              onAddToQueue={() => {}}
              onClose={() => setSelectedAlbum(null)}
              onCollapse={() => setSelectedAlbum(null)}
              isCollapsed={false}
              onUpdateMetadata={undefined}
            />
          </div>
        )}

        {/* Queue Section */}
        <div className="trip-queue">
          <div className="queue-header">
            <h2>🎵 Queue ({queue.length} songs)</h2>
            <div className="queue-actions">
              {selectedSongs.size > 0 && (
                <>
                  <span className="selection-count">
                    {selectedSongs.size} selected
                  </span>
                  <button
                    className="delete-selected-btn"
                    onClick={handleDeleteSelected}
                    title="Delete selected songs"
                  >
                    🗑️ Delete Selected
                  </button>
                  <button
                    className="deselect-all-btn"
                    onClick={handleDeselectAll}
                    title="Deselect all"
                  >
                    ✕ Clear
                  </button>
                </>
              )}
              {queue.length > 0 && selectedSongs.size === 0 && (
                <button
                  className="select-all-btn"
                  onClick={handleSelectAll}
                  title="Select all songs"
                >
                  ☑ Select All
                </button>
              )}
            </div>
          </div>

          {queue.length === 0 ? (
            <div className="queue-empty">
              <p>No songs yet! Browse the library to add music.</p>
            </div>
          ) : (
            <div className="queue-list">
              {queue.map((song, index) => {
                // Determine if this is a Single or Album based on storage path (only for local files)
                const pathParts = song.storagePath?.split('/') || [];
                const type = pathParts[0] || 'Albums';
                const isSingle = song.source === 'local' && type === 'Singles';
                const isSelected = selectedSongs.has(song.id);

                const isDisabled = !isSelected; // Song is disabled if not selected

                return (
                  <div
                    key={song.id}
                    className={`queue-item ${
                      index === tripData.currentTrackIndex ? 'current' : ''
                    } ${isHost ? 'clickable' : ''} ${
                      isSelected ? 'selected' : ''
                    } ${isDisabled ? 'disabled' : ''}`}
                    onClick={() =>
                      isHost && isSelected && handleQueueItemClick(index)
                    }
                    style={{
                      cursor: isHost && isSelected ? 'pointer' : 'default',
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                    title={
                      isHost
                        ? isSelected
                          ? 'Click to play this song'
                          : 'Song disabled - check to enable'
                        : ''
                    }
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleToggleSelection(song.id, e)}
                      onClick={(e) => e.stopPropagation()}
                      className="queue-checkbox"
                      disabled={!isHost}
                      title={
                        isHost
                          ? isSelected
                            ? 'Uncheck to disable song'
                            : 'Check to enable song'
                          : 'Only host can enable/disable songs'
                      }
                    />
                    <div className="queue-row-1">
                      <span className="queue-number">{index + 1}</span>
                      <div className="queue-song-info">
                        <div className="queue-song-title">
                          {song.DisplayTitle || song.title}
                        </div>
                        {song.source && (
                          <span className="queue-source-badge">
                            {song.source === 'local' && '📁'}
                            {song.source === 'youtube' && '▶️'}
                            {song.source === 'spotify' && '🎵'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="queue-row-2">
                      {isSingle ? (
                        <>
                          <div className="queue-album-name">
                            {song.album?.album || song.album || 'Unknown Album'}
                          </div>
                          <button
                            className="queue-delete-btn"
                            onClick={(e) => handleDeleteSong(song.id, index, e)}
                            title="Remove song from queue"
                          >
                            🗑️
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="queue-artist-name">
                            {song.artist || 'Unknown Artist'}
                          </div>
                          <button
                            className="queue-delete-btn"
                            onClick={(e) => handleDeleteSong(song.id, index, e)}
                            title="Remove song from queue"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add Song Modal */}
      {showAddSong && (
        <div className="add-song-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add a Song</h2>
              <button onClick={() => setShowAddSong(false)}>✕</button>
            </div>
            <input
              type="text"
              placeholder="Search for a song..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              autoFocus
            />
            <div className="search-results">
              {searchResults.map((song) => (
                <button
                  key={song.id}
                  className="search-result"
                  onClick={() => handleAddSong(song)}
                >
                  <div className="result-title">{song.title}</div>
                  <div className="result-artist">{song.artist}</div>
                  {song.source && (
                    <div className="result-source">
                      {song.source === 'local' && '📁 Local'}
                      {song.source === 'youtube' && '▶️ YouTube'}
                      {song.source === 'spotify' && '🎵 Spotify'}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Start Button (Host only, waiting status) */}
      {isHost && tripData.status === 'waiting' && queue.length > 0 && (
        <div className="start-trip-bar">
          <button className="start-trip-btn" onClick={handleStartTrip}>
            🚀 Start the Road Trip!
          </button>
        </div>
      )}

      {/* Waiting Status */}
      {tripData.status === 'waiting' && !isHost && (
        <div className="waiting-message">
          <p>⏳ Waiting for host to start the trip...</p>
          <p>Add some songs while you wait!</p>
        </div>
      )}

      {/* Host Transfer Modal */}
      {showHostTransfer && isHost && (
        <div className="add-song-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Transfer Host Control</h2>
              <button onClick={() => setShowHostTransfer(false)}>✕</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '15px', color: '#8892b0' }}>
                Select a participant to transfer host control to:
              </p>
              <div className="participants-list">
                {tripData.participants
                  ?.filter((p) => !p.isHost)
                  .map((p, i) => (
                    <div
                      key={i}
                      className="participant"
                      style={{
                        padding: '1rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => handleTransferHost(p.uid, p.name)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          'rgba(255, 255, 255, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          'rgba(255, 255, 255, 0.05)';
                      }}
                    >
                      <span className="participant-avatar">🎵</span>
                      <span className="participant-name">{p.name}</span>
                      <span style={{ marginLeft: 'auto', color: '#4ade80' }}>
                        Click to transfer →
                      </span>
                    </div>
                  ))}
              </div>
              {tripData.participants?.filter((p) => !p.isHost).length === 0 && (
                <p style={{ color: '#6b7280', textAlign: 'center' }}>
                  No other participants to transfer to.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save Playlist Modal */}
      {/* Chat Component */}
      {showChat && (
        <RoadTripChat
          tripId={trip.id}
          user={user}
          isHost={isHost}
          onClose={() => setShowChat(false)}
        />
      )}

      {/* Video Call Component */}
      {showVideoCall && (
        <VideoCall
          tripId={trip.id}
          user={user}
          participants={tripData.participants || []}
          isHost={isHost}
          onClose={() => setShowVideoCall(false)}
        />
      )}

      {showSavePlaylist && (
        <div className="add-song-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Save Playlist</h2>
              <button onClick={() => {
                setShowSavePlaylist(false);
                setPlaylistName('');
                setSelectedPlaylistId('');
                setIsAppending(false);
              }}>✕</button>
            </div>
            <div style={{ padding: '20px' }}>
              {/* Toggle between Create New and Append to Existing */}
              <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setIsAppending(false);
                    setSelectedPlaylistId('');
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '16px',
                    backgroundColor: !isAppending ? '#667eea' : '#ccc',
                    color: !isAppending ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Create New
                </button>
                <button
                  onClick={() => {
                    setIsAppending(true);
                    setPlaylistName('');
                    loadAvailablePlaylists();
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '16px',
                    backgroundColor: isAppending ? '#667eea' : '#ccc',
                    color: isAppending ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Append to Existing
                </button>
              </div>

              {isAppending ? (
                <>
                  <label style={{ display: 'block', marginBottom: '10px' }}>
                    Select Playlist:
                  </label>
                  <select
                    value={selectedPlaylistId}
                    onChange={(e) => setSelectedPlaylistId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      marginBottom: '15px',
                    }}
                  >
                    <option value="">-- Select a playlist --</option>
                    {availablePlaylists.map((playlist) => (
                      <option key={playlist.id} value={playlist.id}>
                        {playlist.name} ({playlist.trackCount || 0} songs)
                        {playlist.source === 'roadTrip' ? ' 🚗' : ''}
                      </option>
                    ))}
                  </select>
                  <p style={{ marginBottom: '15px', color: '#666' }}>
                    Add {queue.length} song(s) from this Road Trip to the selected playlist.
                    Duplicate songs will be skipped.
                  </p>
                </>
              ) : (
                <>
                  <label style={{ display: 'block', marginBottom: '10px' }}>
                    Playlist Name:
                  </label>
                  <input
                    type="text"
                    placeholder={`e.g., ${tripData.name} Copy`}
                    value={playlistName}
                    onChange={(e) => setPlaylistName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      marginBottom: '15px',
                    }}
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveAsPlaylist();
                      }
                    }}
                  />
                  <p style={{ marginBottom: '15px', color: '#666' }}>
                    This Road Trip is automatically saved as "{tripData.name}".
                    Enter a different name to save a copy with {queue.length}{' '}
                    song(s) that you can play anytime from your Saved Playlists.
                  </p>
                </>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSaveAsPlaylist}
                  disabled={isAppending && !selectedPlaylistId}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '16px',
                    backgroundColor: (isAppending && !selectedPlaylistId) ? '#999' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: (isAppending && !selectedPlaylistId) ? 'not-allowed' : 'pointer',
                  }}
                >
                  💾 {isAppending ? 'Append to Playlist' : 'Save Playlist'}
                </button>
                <button
                  onClick={() => {
                    setShowSavePlaylist(false);
                    setPlaylistName('');
                    setSelectedPlaylistId('');
                    setIsAppending(false);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '16px',
                    backgroundColor: '#ccc',
                    color: 'black',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TripSession;
